"use strict";
/**
 * WP-066: Decodability Validator — shared types and pure validation functions.
 *
 * These types and functions live in `libs/shared/` so both the teacher-portal
 * client (for instant UI feedback) and the sync-server (for authoritative
 * enforcement) use the same validation code. The pure functions operate on
 * pre-fetched data — no DB access, no side effects, deterministic.
 *
 * The server gate is always the source of truth — client-side pre-validation
 * is a UX optimisation, not a security boundary.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DecodabilityValidationError = void 0;
exports.checkWordDecodability = checkWordDecodability;
exports.checkWordsDecodability = checkWordsDecodability;
/**
 * Error thrown when the decodability validator encounters a missing
 * sequence position on an InkStep-tagged activity. This is a typed error,
 * never a silent skip (per rules/02 error handling).
 */
class DecodabilityValidationError extends Error {
    activityId;
    constructor(message, activityId) {
        super(message);
        this.name = 'DecodabilityValidationError';
        this.activityId = activityId;
    }
}
exports.DecodabilityValidationError = DecodabilityValidationError;
/**
 * Check whether a single word is decodable at the given sequence position.
 *
 * A word is decodable if every grapheme it decomposes into is taught at or
 * before the target position (`atSequencePosition`). This is a pure function
 * — it operates on pre-fetched data, no DB access, no side effects.
 *
 * @param word The word to check.
 * @param wordGraphemes The graphemes the word decomposes into (pre-fetched from word bank or decomposed on-the-fly).
 * @param coveredGraphemePositions A map from grapheme → the sequence position where it is taught.
 * @param atSequencePosition The target sequence position (ceiling).
 * @returns `{ passed, violations }` — violations specify the word, grapheme, requiredPosition, and contentPosition.
 */
function checkWordDecodability(word, wordGraphemes, coveredGraphemePositions, atSequencePosition) {
    const violations = [];
    for (const grapheme of wordGraphemes) {
        const requiredPosition = coveredGraphemePositions[grapheme];
        if (requiredPosition === undefined || requiredPosition > atSequencePosition) {
            violations.push({
                word,
                grapheme,
                requiredPosition: requiredPosition ?? -1,
                contentPosition: atSequencePosition,
            });
        }
    }
    return {
        passed: violations.length === 0,
        violations,
    };
}
/**
 * Batch-check multiple words for decodability, skipping exception words.
 *
 * Words in the `exceptionWords` set are skipped (they are admin-approved
 * exceptions or word-bank tricky words). Words not in the `wordGraphemeMap`
 * are flagged as violations with `grapheme: '?'` and `requiredPosition: -1`
 * — this guides the author to add the word to the word bank first.
 *
 * @param words The words to check.
 * @param wordGraphemeMap A map from word → its graphemes (pre-fetched from word bank or decomposed on-the-fly).
 * @param coveredGraphemePositions A map from grapheme → the sequence position where it is taught.
 * @param atSequencePosition The target sequence position (ceiling).
 * @param exceptionWords A Set of words to skip (admin-approved exceptions + word-bank tricky words).
 * @returns `{ passed, violations, exceptionCount, checkedAt }`.
 */
function checkWordsDecodability(words, wordGraphemeMap, coveredGraphemePositions, atSequencePosition, exceptionWords) {
    const allViolations = [];
    let exceptionCount = 0;
    for (const word of words) {
        // Skip exception words (admin-approved or word-bank tricky words)
        if (exceptionWords.has(word)) {
            exceptionCount++;
            continue;
        }
        // Unknown word — not in the grapheme map
        const wordGraphemes = wordGraphemeMap[word];
        if (!wordGraphemes) {
            allViolations.push({
                word,
                grapheme: '?',
                requiredPosition: -1,
                contentPosition: atSequencePosition,
            });
            continue;
        }
        // Check the word's graphemes against the sequence positions
        const result = checkWordDecodability(word, wordGraphemes, coveredGraphemePositions, atSequencePosition);
        allViolations.push(...result.violations);
    }
    return {
        passed: allViolations.length === 0,
        violations: allViolations,
        exceptionCount,
        checkedAt: new Date().toISOString(),
    };
}
